import { Box, Button, styled, Typography, Tabs, Tab } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo } from "react";
import { getAllOffers } from "../../API/API";
import { showToast } from "../../components/common/Util/Util";
import React from "react";

const PageWrapper = styled(Box)({
  backgroundColor: "#F5F6F7",
  minHeight: "100vh",
  padding: 0,
  margin: 0,
  display: "flex",
  justifyContent: "center",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#FFFFFF",
  width: "100%",
  maxWidth: "1200px",
  padding: "32px",
  boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.05)",
  border: "1px solid #E5E8EB",
  margin: "24px 16px",
  borderRadius: "20px",
  transition: "all 0.3s ease",
});

const PageTitle = styled(Typography)({
  color: "#0C53B7",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "120%",
  letterSpacing: "-0.2px",
  marginBottom: "12px",
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: "-4px",
    left: "0",
    width: "60px",
    height: "3px",
    backgroundColor: "#0052CC",
    borderRadius: "2px",
  },
});

const ProposalCard = styled(Box)({
  border: "1px solid #E5E8EB",
  borderRadius: "16px",
  padding: "32px",
  marginBottom: "24px",
  position: "relative",
  transition: "all 0.3s ease",
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-2px)",
  },
});

const StatusBadge = styled(Box)(({ status }) => ({
  position: "absolute",
  top: "24px",
  right: "24px",
  borderRadius: "24px",
  padding: "8px 16px",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "100px",
  textAlign: "center",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
  backgroundColor: status === "accepted" ? "#E9FCD4" : 
                 status === "rejected" ? "#FFE7D9" : "#FFF7CD",
  color: status === "accepted" ? "#229A16" : 
         status === "rejected" ? "#B72136" : "#7A4F01",
  "&:before": {
    content: '""',
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: status === "accepted" ? "#229A16" : 
                    status === "rejected" ? "#B72136" : "#7A4F01",
    display: "inline-block"
  },
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
  }
}));

const DetailGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr) auto",
  alignItems: "end",
  marginTop: "32px",
  gap: "32px",
  paddingRight: "24px",
  "@media (max-width: 1200px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "24px",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
});

const RejectionDetails = styled(Box)({
  marginTop: "24px",
  padding: "16px",
  backgroundColor: "#FFF4F2",
  borderRadius: "12px",
  border: "1px solid #FFE7D9",
});

const RejectionTitle = styled(Typography)({
  color: "#B72136",
  fontSize: "16px",
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  marginBottom: "16px",
});

const RejectionGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "24px",
  "@media (max-width: 900px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
  },
});

const LabelText = styled(Typography)({
  color: "#637381",
  fontSize: "14px",
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  marginBottom: "8px",
  transition: "color 0.3s ease",
});

const ValueText = styled(Typography)({
  color: "#212B36",
  fontSize: "20px",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 600,
  lineHeight: "150%",
  letterSpacing: "0.2px",
  whiteSpace: "nowrap",
  transition: "color 0.3s ease",
});

const ViewOfferButton = styled(Button)({
  backgroundColor: "#0052CC",
  color: "white",
  borderRadius: "12px",
  padding: "12px 24px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  height: "48px",
  minWidth: "140px",
  transition: "all 0.3s ease",
  boxShadow: "0px 2px 4px rgba(0, 82, 204, 0.15)",
  "&:hover": {
    backgroundColor: "#003E99",
    boxShadow: "0px 4px 8px rgba(0, 82, 204, 0.25)",
    transform: "translateY(-1px)",
  },
  "@media (max-width: 600px)": {
    width: "100%",
  },
});

const ProposalList = ({ proposals, onViewOffer }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return proposals.map((proposal) => {
    const facilities = Array.isArray(proposal.offer?.facilities)
      ? proposal.offer.facilities
      : [];
    const facilitiesLoanTotal = facilities.reduce(
      (sum, facility) => sum + Number(facility.loanOffered || 0),
      0
    );
    const derivedLoanAmount =
      facilitiesLoanTotal ||
      Number(proposal.offer?.loanOffered ?? proposal.loanOffered ?? 0) ||
      0;

    return (
      <ProposalCard key={proposal.offer?.id || proposal.id}>
        <StatusBadge status={(proposal.offer?.offerStatus || proposal.offerStatus)?.toLowerCase()}>
          {(proposal.offer?.offerStatus || proposal.offerStatus) ? (proposal.offer?.offerStatus || proposal.offerStatus).charAt(0).toUpperCase() + (proposal.offer?.offerStatus || proposal.offerStatus).slice(1) : "Pending"}
        </StatusBadge>
        <DetailGrid>
          <Box>
            <LabelText>Loan Amount</LabelText>
            <ValueText>₹{derivedLoanAmount.toLocaleString()}</ValueText>
          </Box>
          <Box>
            <LabelText>Interest Rate</LabelText>
            <ValueText>{proposal.offer?.rateOfIntrest || proposal.rateOfIntrest}%</ValueText>
          </Box>
          <Box>
            <LabelText>Tenure</LabelText>
            <ValueText>{proposal.offer?.repaymentTenure || proposal.repaymentTenure} months</ValueText>
          </Box>
          <Box>
            <LabelText>Processing Fee</LabelText>
            <ValueText>₹{(Number(proposal.offer?.processingFees || proposal.processingFees) || 0).toLocaleString()}</ValueText>
          </Box>
          <ViewOfferButton onClick={() => onViewOffer(proposal.offer?.id || proposal.id)}>
            View Offer
          </ViewOfferButton>
        </DetailGrid>

      {(proposal.offer?.offerStatus || proposal.offerStatus)?.toLowerCase() === "rejected" && (
        <RejectionDetails>
          <RejectionTitle>Rejection Details</RejectionTitle>
          <RejectionGrid>
            <Box>
              <LabelText>Rejected By</LabelText>
              <ValueText sx={{ fontSize: "16px", color: "#364152" }}>
                {proposal.offer?.rejectedBy || proposal.rejectedBy || "N/A"}
              </ValueText>
            </Box>
            <Box>
              <LabelText>Rejected Date</LabelText>
              <ValueText sx={{ fontSize: "16px", color: "#364152" }}>
                {formatDate(proposal.offer?.rejectedDate || proposal.rejectedDate)}
              </ValueText>
            </Box>
            <Box>
              <LabelText>Rejection Reason</LabelText>
              <ValueText sx={{ fontSize: "16px", color: "#364152" }}>
                {proposal.offer?.rejectedReason || proposal.rejectedReason || "No reason provided"}
              </ValueText>
            </Box>
          </RejectionGrid>
        </RejectionDetails>
      )}
      </ProposalCard>
    );
  });
};

const ProposalCardList = React.memo(ProposalList);

const EmptyState = (activeTab) => (
  <Box
    sx={{
      textAlign: "center",
      py: 8,
      px: 2,
      borderRadius: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
    }}
  >
    <img
      src="/No_Loan_Application.png"
      alt="No proposals yet"
      style={{
        width: "180px",
        height: "180px",
        marginBottom: "24px",
      }}
    />
    <Typography
      variant="h6"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        fontSize: "24px",
        color: "#212B36",
        mb: 1,
        letterSpacing: "-0.02em",
      }}
    >
      {activeTab === 0 ? "No offers yet" : 
       activeTab === 1 ? "No accepted offer yet" :
       "No rejected offers yet"}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "14px",
        lineHeight: "24px",
        color: "#637381",
        maxWidth: "400px",
        mb: 3,
        textAlign: "center",
      }}
    >
      {activeTab === 2 ? 
        "There are no rejected loan offers to display at this time." :
        "You haven't accepted any loan offers yet. Once you accept a offer, it will appear here."}
    </Typography>
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        justifyContent: "center",
      }}
    >
      <Button
        variant="contained"
        onClick={() => (window.location.href = "/dashboard")}
        sx={{
          backgroundColor: "#0052CC",
          "&:hover": {
            backgroundColor: "#0047B3",
          },
          color: "white",
          textTransform: "none",
          borderRadius: "8px",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
          fontSize: "15px",
          padding: "10px 24px",
          height: "48px",
          boxShadow: "none",
        }}
      >
        Go to Dashboard
      </Button>
    </Box>
  </Box>
);

const AcceptedProposals = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);

  const userId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.ID;
    } catch (error) {
      showToast("Error accessing user data", "error");
      return null;
    }
  }, []);

  const fetchProposals = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const status = activeTab === 1 ? "accepted" : 
                    activeTab === 2 ? "rejected" : null;
      const response = await getAllOffers(userId, status);
      const rawProposals = response?.result?.result || [];
      const normalizedProposals = rawProposals.map((item) => {
        const offer = item?.offer || {};
        const facilities = Array.isArray(offer.facilities) ? offer.facilities : [];
        if (!facilities.length) {
          return item;
        }

        const primaryFacility =
          facilities.find((facility) => Number(facility.loanOffered || 0) > 0) ||
          facilities[0];

        const aggregatedLoan = facilities.reduce(
          (sum, facility) => sum + Number(facility.loanOffered || 0),
          0
        );

        const aggregatedProcessingFees = facilities.reduce(
          (sum, facility) => sum + Number(facility.processingFees || 0),
          0
        );

        return {
          ...item,
          offer: {
            ...offer,
            loanOffered:
              Number(
                offer.loanOffered ??
                  (aggregatedLoan || primaryFacility?.loanOffered || 0)
              ) || 0,
            rateOfIntrest:
              offer.rateOfIntrest ?? primaryFacility?.rateOfIntrest ?? 0,
            repaymentTenure:
              offer.repaymentTenure ?? primaryFacility?.repaymentTenure ?? 0,
            processingFees:
              Number(
                offer.processingFees ??
                  (aggregatedProcessingFees ||
                    primaryFacility?.processingFees ||
                    0)
              ) || 0,
            miscellaneousCharges:
              Number(
                offer.miscellaneousCharges ??
                  primaryFacility?.miscellaneousCharges ??
                  0
              ) || 0,
            emi: Number(offer.emi ?? primaryFacility?.emi ?? 0) || 0,
          },
        };
      });
      setProposals(normalizedProposals);
    } catch (error) {
      showToast("Failed to fetch proposals", "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [userId, activeTab]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  return (
    <PageWrapper>
      <ContentContainer>
        <PageTitle>Offers</PageTitle>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="All Offers" />
        <Tab label="Accepted Offers" />         
          <Tab label="Rejected Offers" />
        </Tabs>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : proposals.length > 0 ? (
          <ProposalCardList 
            proposals={proposals}
            onViewOffer={(id) => navigate(`/proposal-details/${id}`)}
          />
        ) : (
          <EmptyState activeTab={activeTab} />
        )}
      </ContentContainer>
    </PageWrapper>
  );
};

export default React.memo(AcceptedProposals);
